<script lang="ts">
import { defineComponent } from 'vue'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import type ResponseData from '@/types/ResponseData'
import DashboardDataService from '@/services/DashboardDataService'
import type DashboardDataset from '@/types/DashboardDataset'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default defineComponent({
  name: 'dashboard-component',
  components: { Bar },
  data() {
    return {
      loaded: false,
      usersChartDataSets: [] as DashboardDataset[],
      companiesChartDataSets: [] as DashboardDataset[],
      mailsChartDataSets: [] as DashboardDataset[],
      ordersChartDataSets: [] as DashboardDataset[],

      usersChartData: {
        labels: [] as string[],
        datasets: [
          {
            label: '',
            backgroundColor: '#f87979',
            data: []
          }
        ]
      },
      companiesChartData: {
        labels: [] as string[],
        datasets: [
          {
            label: '',
            backgroundColor: '#f87979',
            data: []
          }
        ]
      },
      mailsChartData: {
        labels: [] as string[],
        datasets: [
          {
            label: '',
            backgroundColor: '#f87979',
            data: []
          }
        ]
      },
      ordersChartData: {
        labels: [] as string[],
        datasets: [
          {
            label: '',
            backgroundColor: '#f87979',
            data: []
          }
        ]
      },
    }
  },
  methods: {
    getLabels(datasets: any) {
      const array = [] as string[]
      datasets.forEach(function (value: any) {
        const year = value.label // Year from the dataset
        value.labels.split(',').forEach(function (label: string) {
          let monthName = ''
          switch (label) {
            case '1':
              monthName = 'Január'
              break
            case '2':
              monthName = 'Február'
              break
            case '3':
              monthName = 'Marec'
              break
            case '4':
              monthName = 'Apríl'
              break
            case '5':
              monthName = 'Máj'
              break
            case '6':
              monthName = 'Jún'
              break
            case '7':
              monthName = 'Júl'
              break
            case '8':
              monthName = 'August'
              break
            case '9':
              monthName = 'September'
              break
            case '10':
              monthName = 'Október'
              break
            case '11':
              monthName = 'November'
              break
            case '12':
              monthName = 'December'
              break
          }
          if (monthName) {
            const labelWithYear = `${monthName} ${year}`
            if (!array.includes(labelWithYear)) array.push(labelWithYear)
          }
        })
      })
      return array
    },
    getDatasets(datasets: any, allLabels: string[]) {
      const array = [] as any[]
      datasets.forEach((yearDataset: any) => {
        const year = yearDataset.label
        const months = yearDataset.labels.split(',')
        const dataValues = yearDataset.data.split(',')

        // Create a map of month -> data for this year
        const dataMap: { [key: string]: string } = {}
        months.forEach((month: string, index: number) => {
          let monthName = ''
          switch (month) {
            case '1': monthName = 'Január'; break
            case '2': monthName = 'Február'; break
            case '3': monthName = 'Marec'; break
            case '4': monthName = 'Apríl'; break
            case '5': monthName = 'Máj'; break
            case '6': monthName = 'Jún'; break
            case '7': monthName = 'Júl'; break
            case '8': monthName = 'August'; break
            case '9': monthName = 'September'; break
            case '10': monthName = 'Október'; break
            case '11': monthName = 'November'; break
            case '12': monthName = 'December'; break
          }
          const labelWithYear = `${monthName} ${year}`
          dataMap[labelWithYear] = dataValues[index]
        })

        // Align data with all labels
        const alignedData = allLabels.map(label => dataMap[label] || '0')

        array.push({
          data: alignedData,
          label: year,
          backgroundColor: yearDataset.backgroundColor,
        })
      })

      return array
    },
    getAllData() {
      DashboardDataService.getAllDashboardData()
        .then((response: ResponseData) => {
          this.usersChartDataSets = response.data.users
          this.companiesChartDataSets = response.data.companies
          this.mailsChartDataSets = response.data.mails
          this.ordersChartDataSets = response.data.orders

          const usersLabels = this.getLabels(response.data.users)
          const companiesLabels = this.getLabels(response.data.companies)
          const mailsLabels = this.getLabels(response.data.mails)
          const ordersLabels = this.getLabels(response.data.orders)

          this.usersChartData = {
            labels: usersLabels,
            datasets: this.getDatasets(this.usersChartDataSets, usersLabels),
          }
          this.companiesChartData = {
            labels: companiesLabels,
            datasets: this.getDatasets(this.companiesChartDataSets, companiesLabels),
          }
          this.mailsChartData = {
            labels: mailsLabels,
            datasets: this.getDatasets(this.mailsChartDataSets, mailsLabels),
          }
          this.ordersChartData = {
            labels: ordersLabels,
            datasets: this.getDatasets(this.ordersChartDataSets, ordersLabels),
          }

          this.loaded = true
        })
        .catch((e: Error) => {
          this.loaded = false
          toast.error('Chyba: ' + e.message)
        })
    },
  },
  mounted() {
    this.getAllData()
  },
})
</script>


<template>
  <main class="flex-1">
    <div class="py-8 px-4 max-w-7xl mx-auto">
      <h1 class="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
        <svg class="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" stroke-width="2" fill="none" />
          <path d="M7 9h10M7 15h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        Admin Dashboard
      </h1>
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
        <!-- Companies -->
        <div class="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex flex-col items-center min-h-[340px]">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="text-lg font-semibold text-slate-800">Spoločnosti</span>
          </div>
          <div class="w-full h-64 flex items-center justify-center">
            <Bar v-if="loaded && companiesChartData" :chart-data="companiesChartData" style="width:100%;height:100%;" />
          </div>
        </div>
        <!-- Users -->
        <div class="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex flex-col items-center min-h-[340px]">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M4 20c0-4 16-4 16 0" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
            <span class="text-lg font-semibold text-slate-800">Klienti</span>
          </div>
          <div class="w-full h-64 flex items-center justify-center">
            <Bar v-if="loaded && usersChartData" :chart-data="usersChartData" style="width:100%;height:100%;" />
          </div>
        </div>
        <!-- Orders -->
        <div class="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex flex-col items-center min-h-[340px]">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M7 7V5a5 5 0 0 1 10 0v2" stroke="currentColor" stroke-width="2" fill="none" />
              <circle cx="8.5" cy="15.5" r="1.5" stroke="currentColor" stroke-width="2" fill="none" />
              <circle cx="15.5" cy="15.5" r="1.5" stroke="currentColor" stroke-width="2" fill="none" />
              <rect x="7" y="10" width="10" height="2" rx="1" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
            <span class="text-lg font-semibold text-slate-800">Objednávky</span>
          </div>
          <div class="w-full h-64 flex items-center justify-center">
            <Bar v-if="loaded && ordersChartData" :chart-data="ordersChartData" style="width:100%;height:100%;" />
          </div>
        </div>
        <!-- Mails -->
        <div class="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex flex-col items-center min-h-[340px]">
          <div class="flex items-center gap-2 mb-4">
            <svg class="w-6 h-6 text-pink-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M3 7l9 6 9-6" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
            <span class="text-lg font-semibold text-slate-800">Zásielky</span>
          </div>
          <div class="w-full h-64 flex items-center justify-center">
            <Bar v-if="loaded && mailsChartData" :chart-data="mailsChartData" style="width:100%;height:100%;" />
          </div>
        </div>
      </div>
      <div v-if="!loaded" class="fixed inset-0 bg-white/60 flex items-center justify-center z-50">
        <svg class="animate-spin h-10 w-10 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
        </svg>
      </div>
    </div>
  </main>
</template>
